/**
 * LoginPage — replaces LoginCtrl + templates/login.html.
 *
 * Original template had two <input type="number"> fields (licence, pincode)
 * inside a centred 33%-width column.  Replicated with Mantine's Stack/Paper.
 *
 * One layout in every Size class: a card centred in a page region that scrolls when
 * the card doesn't fit. The region ends at the on-screen keyboard, and focus on Pin
 * brings Log In above it, because the iPhone numeric keypad has no return key.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Center,
  Image,
  LoadingOverlay,
  Paper,
  PasswordInput,
  rem,
  Stack,
  TextInput,
} from '@mantine/core';

import { useAuth } from '@/contexts/AuthContext';
import { useSizeClass } from '@/hooks/useSizeClass';
import { useVisualViewport } from '@/hooks/useVisualViewport';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { shortHeight } = useSizeClass();
  const { height, offsetTop } = useVisualViewport();

  const [licence, setLicence] = useState('');
  const [pincode, setPincode] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [pinFocused, setPinFocused] = useState(false);
  const logInRef = useRef<HTMLButtonElement>(null);

  // The keyboard opens after Pin takes focus, so this runs again once the region has
  // shrunk to the space above it or moved with the visual viewport.
  useEffect(() => {
    if (pinFocused) logInRef.current?.scrollIntoView({ block: 'end' });
  }, [pinFocused, height, offsetTop]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setLoading(true);
    try {
      await login(licence, pincode);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  // The logo and the spacing around it are chrome, so Short height shrinks them, and the
  // fields, their labels and Log In keep their size. The spacing below is as tight as it
  // is so the card fits a phone in landscape in a browser tab, which is 334px high.
  const logoSize = shortHeight ? 64 : 140;

  return (
    <Box
      component="main"
      h="100%"
      // The shell keeps its full height while the keyboard is open, so the region takes
      // the visual viewport's top and stops at its height. The shell's top inset sits
      // above the region and comes off that height.
      mah={`calc(${height}px - env(safe-area-inset-top))`}
      pos="relative"
      top={offsetTop}
      style={{ overflowY: 'auto' }}
    >
      {/* Auto margins centre the card when it fits and collapse when it doesn't, so a
          tall card starts at the top and the logo can be scrolled to. */}
      <Box mih="100%" px="md" py={shortHeight ? 4 : 'md'} style={{ display: 'flex' }}>
        <Paper
          shadow="md"
          p={shortHeight ? 'md' : 'xl'}
          w="100%"
          maw={400}
          m="auto"
          pos="relative"
          data-testid="login-card"
        >
          <LoadingOverlay visible={loading} overlayProps={{ blur: 2 }} />
          <Stack gap={shortHeight ? 0 : 'md'}>
            <Center>
              <Image
                src="/img/logo.png"
                alt="EcoClear Garden Bags"
                w={logoSize}
                h={logoSize}
                fit="contain"
              />
            </Center>

            <form onSubmit={handleSubmit}>
              <Stack gap={shortHeight ? 'xs' : 'sm'}>
                <TextInput
                  label="Licence Number"
                  placeholder="Licence"
                  inputMode="numeric"
                  enterKeyHint="next"
                  value={licence}
                  onChange={e => {
                    setLicence(e.currentTarget.value);
                    setErrorMsg(null);
                  }}
                  autoFocus
                  required
                />
                <PasswordInput
                  label="Pin Code"
                  placeholder="Pin Code"
                  inputMode="numeric"
                  enterKeyHint="go"
                  value={pincode}
                  onChange={e => {
                    setPincode(e.currentTarget.value);
                    setErrorMsg(null);
                  }}
                  onFocus={() => setPinFocused(true)}
                  onBlur={() => setPinFocused(false)}
                  // Mantine's show/hide button is 32px at `lg`, under the Touch floor.
                  vars={() => ({ root: { '--psi-button-size': rem(44) } })}
                  required
                />

                {errorMsg && (
                  <Alert
                    color="red"
                    title="Login Failed"
                    // The error is body text, and Mantine's Alert text is 14px.
                    styles={{
                      title: { fontSize: 'var(--mantine-font-size-md)' },
                      message: { fontSize: 'var(--mantine-font-size-md)' },
                    }}
                  >
                    {errorMsg}
                  </Alert>
                )}

                <Button ref={logInRef} type="submit" fullWidth color="dark" disabled={loading}>
                  Log In
                </Button>
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
